import React, { useEffect, useState } from "react";
import { useApp, AppScreen } from "../components/AppContext";
import { useCurrentUser } from "../api";
import { useGetExtra } from "../api/extras/useGetExtra";

import { useGetExtraEvents } from "../api/extraEvents/useGetExtraEvents";
import { useTranslation } from "../i18n/TranslationContext";

export function OnboardingWelcome() {
	const {
		navigateDirectly,
		updateOnboardingState,
		getOnboardingSequence,
		userCompletionState,
	} = useApp();
	const { user } = useCurrentUser();
	const { extra, loading: profileLoading } = useGetExtra(user?.extraId);
	const { events, loading: eventsLoading } = useGetExtraEvents(
		user?.extraId || 0
	);
	const { t } = useTranslation();

	interface OnboardingStep {
		step: number;
		id: string;
		title: string;
		route: AppScreen;
		completed: boolean;
		nextStep: number | null;
	}

	const [dynamicSequence, setDynamicSequence] = useState<OnboardingStep[]>([]);

	// Build static onboarding sequence - same for all users
	useEffect(() => {
		if (!profileLoading && !eventsLoading) {
			const sequence: OnboardingStep[] = [];
			let stepNumber = 1;

			// Always show these three steps for all users (internal and external)
			// 1. Profile
			sequence.push({
				step: stepNumber++,
				id: "profile",
				title: t("onboardingWelcome.steps.profile"),
				route: "onboarding-step-profile",
				completed: false,
				nextStep: null, // Will be updated later
			});

			// 2. Code of conduct
			sequence.push({
				step: stepNumber++,
				id: "code-of-conduct",
				title: t("onboardingWelcome.steps.codeOfConduct"),
				route: "onboarding-step-code-of-conduct",
				completed: false,
				nextStep: null, // Will be updated later
			});

			// 3. Events
			sequence.push({
				step: stepNumber++,
				id: "event-application",
				title: t("onboardingWelcome.steps.eventApplication"),
				route: "onboarding-step-event-application",
				completed: false,
				nextStep: null, // Will be updated later
			});

			// Update nextStep values
			sequence.forEach((step, index) => {
				step.nextStep =
					index < sequence.length - 1 ? sequence[index + 1].step : null;
			});

			setDynamicSequence(sequence);
		}
	}, [eventsLoading, profileLoading, t]);

	const onboardingSequence: OnboardingStep[] =
		dynamicSequence.length > 0
			? dynamicSequence
			: getOnboardingSequence().map((step) => ({
					...step,
					completed: false,
					nextStep: step.nextStep ?? null,
				}));

	const handleGetStarted = () => {
		// Always start from the first step
		const firstStep = onboardingSequence[0];

		if (firstStep) {
			updateOnboardingState({
				currentOnboardingStep: firstStep.step,
			});
			navigateDirectly(firstStep.route);
		}
	};

	const handleSkipOnboarding = () => {
		updateOnboardingState({
			hasCompletedOnboarding: true,
			isFirstTimeUser: false,
		});
		navigateDirectly("dashboard");
	};

	// Handle step click to navigate directly to that step
	const handleStepClick = (step: OnboardingStep) => {
		updateOnboardingState({ currentOnboardingStep: step.step });
		navigateDirectly(step.route);
	};

	// Show loading state while fetching profile or events
	if (profileLoading || eventsLoading) {
		return (
			<div className="onboarding-welcome-container">
				<div className="welcome-content">
					<div className="flex flex-col items-center justify-center min-h-[60vh]">
						<div className="w-8 h-8 border-4 border-[var(--ucm-primary-teal)] border-t-transparent rounded-full animate-spin mb-4"></div>
						<p className="text-[var(--ucm-text-medium)]">
							{t("onboardingWelcome.loadingProfile")}
						</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="onboarding-welcome-container">
			<div className="welcome-content">
				<div className="welcome-icon">
					<span className="text-6xl">👋</span>
				</div>

				<h1 className="text-3xl font-bold text-[var(--ucm-text-dark)] mb-4 text-center">
					{t("onboardingWelcome.welcome")}
				</h1>

				<p className="text-[var(--ucm-text-medium)] text-center mb-8 leading-relaxed">
					{t("onboardingWelcome.setupInMinutes")}
				</p>

				<div className="onboarding-steps-preview">
					<div className="steps-list">
						{onboardingSequence.map((step) => (
							<div
								key={step.step}
								className="step-preview cursor-pointer hover:bg-[var(--ucm-border-light)] rounded-lg transition-colors duration-200 p-2 -m-2"
								onClick={() => handleStepClick(step)}
							>
								<div className="step-number">
									<span className="text-sm font-semibold">{step.step}</span>
								</div>
								<span className="step-name text-[var(--ucm-text-dark)]">
									{step.title}
								</span>
							</div>
						))}
					</div>
				</div>

				<div className="welcome-actions">
					<button
						onClick={handleGetStarted}
						className="primary-button w-full bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-full hover:bg-[var(--ucm-primary-teal-hover)] transition-colors duration-200"
					>
						{t("onboardingWelcome.getStarted")}
					</button>

					<button
						onClick={handleSkipOnboarding}
						className="secondary-button w-full bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-dark)] font-semibold rounded-full hover:bg-[var(--ucm-border-medium)] transition-colors duration-200"
					>
						{t("onboardingWelcome.skipSetup")}
					</button>
				</div>
			</div>
		</div>
	);
}
